import { TripClassPrice } from 'Services/Api/_HumanWritten/PricingService/PricingService';
import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { SelectedTrips } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardWrap';
import { FilteredTripCards } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Tickets/FilteredTripCards';
import Tabs, { ITabConfig } from 'Views/Components/Tabs/Tabs';
import { action } from 'mobx';
import { wizardModeOptions } from 'Models/Enums';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { TicketsTabTrip } from 'Services/Api/_HumanWritten/BookingWizardDataService';
import {
	keepReturnDayAfterDeparture,
	unselectInvalidDateTicket,
} from 'Util/_HumanWritten/TicketSelectionValidationUtils';
import { daysOfWeekLong, getDayOfTheWeek } from 'Util/_HumanWritten/DayOfWeekUtils';
import { ferryTripFilterDepartedTicketTab } from 'Util/_HumanWritten/TicketSelectionUtils';
import If from 'Views/Components/If/If';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import CustomSpinner from 'Views/Components/Spinner/CustomSpinner';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { Button, Colors, Display, Sizes } from 'Views/Components/Button/Button';
import { checkLeftDate, getNewDate } from 'Util/_HumanWritten/DateSelectorUtils';
import { getMinimumFerryTicketPriceFromListOfFerryTrips } from 'Util/_HumanWritten/PriceCalculations/PriceFunctionSelectors/GetMinimumFerryTicketPriceFromListOfFerryTrips';
import { cheapestClassTotal, splitPartyTotal } from './tripClassFares';
import { mixGroups, mixedTravellers, windowClasses } from './mixedFares';
import { classColour } from 'Modules/Ferries/Utils/seatGrid';
import { selectedClassId } from './TripFareColumns';
import { appConfigStore } from 'Models/AppConfigStore';
import FareDayRail, { FareDay } from './FareDayRail';
import { useNextSailingQuery } from './Hooks/useNextSailingQuery';
import { useBookingWizardDataStore } from '../../BookingWizardDataStore';
import {
	BookingWizardData,
	passengerCountsByType,
	passengerFaresByLeg,
} from '../../BookingWizardData/BaseBookingWizardData';

export interface TripSelectionCalendarProps {
	ferryTrips: TicketsTabTrip[] | null;
	departure: boolean;
	selectedTrips: SelectedTrips;
	isStaff: boolean;
	refreshTrips?: () => void;
	priceByFerryTrip: { [key: string]: number };
	classPricesByFerryTrip?: { [key: string]: TripClassPrice[] };
	pricesLoading: boolean;
	tripsLoading: boolean;
	currentTab?: number;
	setCurrentTab?: (tabNumber: number) => void;
}

export function TripSelectionCalendar({
	ferryTrips,
	departure,
	selectedTrips,
	isStaff,
	refreshTrips,
	priceByFerryTrip,
	classPricesByFerryTrip,
	pricesLoading,
	tripsLoading,
	currentTab,
	setCurrentTab,
}: TripSelectionCalendarProps) {
	const { wizardData, setWizardData } = useBookingWizardDataStore();
	const intl = useIntl();

	/*
		`Show Thu 8 Oct — from $96.00`, or just the date where the day prices nothing.

		One place, because the forward and backward buttons word it identically and a day with no
		price has to drop the whole clause rather than print a dangling "from".
	*/
	const showDayLabel = (date: Date | string, price: number | null) =>
		(price !== null
			? intl.formatMessage(
					{ id: 'trip-option__show-day-from__action', defaultMessage: 'Show {date} — from {price}' },
					{ date: moment(date).format('ddd D MMM'), price: formatPriceDisplay(price, false) },
				)
			: intl.formatMessage(
					{ id: 'trip-option__show-day__action', defaultMessage: 'Show {date}' },
					{ date: moment(date).format('ddd D MMM') },
				));

	const listOfTabs: ITabConfig[] = [];
	// Built alongside the tabs so the rail and the tabs cannot disagree about which day is
	// which — same loop, same order, same index.
	const days: FareDay[] = [];
	const passengerCounts = React.useMemo(() => passengerCountsByType(wizardData), [wizardData]);

	// Whether this window's vessels price their classes differently. Taken across the whole window
	// rather than per day, so the layout does not change shape as the booker steps between days.
	const showFareColumns = React.useMemo(() => {
		const columns =
			(ferryTrips ?? []).map(trip => classPricesByFerryTrip?.[trip.id] ?? []).find(prices => prices.length > 0) ??
			[];
		/*
			The setting, and a window that actually prices fare types.

			`columns` was computed and then thrown away, so the setting alone decided the shape.
			With fare types switched on for a site whose vessel has none, the fare-priced day rail
			was used anyway — and its prices come from the class prices, which are empty — so every
			day on the calendar read `No trips` while the sailings were listed underneath.

			Taken across the whole window rather than per day, so the layout does not change shape as
			the booker steps between days. There is no flicker to guard against: the section above
			holds the calendar back until the class prices have arrived.

			Freight is the one exception: it has no passengers to put in a fare type.

			A window with no sailings at all keeps the rail too. It has no columns because it has no
			trips, not because the vessel has no fare types — falling back drew the old day tabs for
			the one window and the rail again for the next, and the rail is also what offers the next
			sailing when there is none nearby.
		*/
		return (
			appConfigStore.passengerClassesEnabled &&
			wizardData.tabSelected !== 'freight' &&
			(columns.length > 0 || (ferryTrips ?? []).length === 0)
		);
	}, [ferryTrips, classPricesByFerryTrip, passengerCounts, wizardData.tabSelected]);

	/**
	 * The old week-of-tabs day strip.
	 *
	 * @deprecated Replaced by `FareDayRail`, which every booking type now uses — a site with no fare
	 * types gets the same rail, the same empty-day panel and the same next-sailing lookahead as one
	 * that sells by class. The tab strip below is kept, unreachable, so the two can be compared and
	 * the rail reverted in one line if the redesign has to come back out. Nothing should be built
	 * against it.
	 */
	const USE_LEGACY_DAY_TABS: boolean = false;
	const legType = departure ? 'departure' : 'return';

	/**
	 * The booking's travellers and their fare types, where they are not all in the same one.
	 *
	 * <p>Above the days rather than beside the sailings, because it is true of the whole step: every
	 * day, every sailing and every column below reads differently once the party is split, and a note
	 * repeated inside the table looked like a remark about one of them.</p>
	 */
	const mixedParty = React.useMemo(() => {
		if (!appConfigStore.passengerClassesEnabled || wizardData.tabSelected === 'freight') {
			return null;
		}
		if (selectedClassId(wizardData, legType) !== 'MIXED') {
			return null;
		}
		// Names come from whichever of the window's sailings prices its classes — they run on the
		// same vessels, so any of them names the fare types the booking is in.
		const columns =
			(ferryTrips ?? []).map(trip => classPricesByFerryTrip?.[trip.id] ?? []).find(prices => prices.length > 0) ??
			[];
		if (columns.length === 0) {
			return null;
		}
		return mixGroups(mixedTravellers(wizardData, legType), windowClasses(columns, classPricesByFerryTrip));
	}, [wizardData, legType, ferryTrips, classPricesByFerryTrip]);

	const departureReturnDate = departure ? wizardData.startDate : wizardData.endDate;
	const firstDay = moment(departureReturnDate).add(-2, 'days').toDate().getDay();
	const firstDate = moment(departureReturnDate).add(-2, 'days').toDate();

	const leftTab = (
		<Button
			className="move-date-left-button"
			display={Display.Text}
			colors={Colors.Alternate}
			sizes={Sizes.ExtraLarge}
			icon={{ icon: 'chevron-left', iconPos: 'icon-left' }}
			onClick={() => changeDateByWeek(false)}
			disabled={isStaff ? false : checkLeftDate(departureReturnDate as Date, 1)}
		/>
	);
	const rightTab = (
		<Button
			className="move-date-right-button"
			display={Display.Text}
			colors={Colors.Alternate}
			sizes={Sizes.ExtraLarge}
			icon={{ icon: 'chevron-right', iconPos: 'icon-right' }}
			onClick={() => changeDateByWeek(true)}
		/>
	);

	// Check if the proposed change of 5 days is a valid change, if it is not return todays date
	const changeDateByWeek = action((increase: boolean) => {
		const newData = new BookingWizardData({ ...wizardData });
		if (departure) {
			newData.startDate = getNewDate(departureReturnDate as Date, increase, 5, isStaff);
			newData.ticketSelectionStartDate = newData.startDate;
			unselectInvalidDateTicket(newData);
		} else {
			newData.endDate = getNewDate(departureReturnDate as Date, increase, 5, isStaff);
			newData.ticketSelectionEndDate = newData.endDate;
		}

		setWizardData(newData);
	});

	for (let i = 0, index = firstDay; i < daysOfWeekLong.length - 2; i++, index++) {
		if (index === 7) {
			index = 0;
		}
		const day = getDayOfTheWeek(index, false);
		const currentDay = new Date(firstDate);
		currentDay.setDate(firstDate.getDate() + i);

		const filteredFerryList = ferryTripFilterDepartedTicketTab(
			ferryTrips ?? [],
			currentDay,
			wizardData.tabSelected,
			isStaff,
		);

		const filteredPriceByFerryTrip = Object.keys(priceByFerryTrip)
			.filter(key => filteredFerryList.map(x => x.id).includes(key))
			.reduce(
				(obj, key) => {
					obj[key] = priceByFerryTrip[key];
					return obj;
				},
				{} as { [key: string]: number },
			);

		const tabName = (
			<div
				className={classNames('filtered-trip-calendar-header', {
					'no-trips': filteredFerryList?.length === 0,
				})}
			>
				<p className="date-header">{moment(currentDay).format('DD')}</p>
				<p className="day-header">{day.toUpperCase()}</p>
				<If condition={tripsLoading && wizardData.tabSelected === 'freight'}>
					<CustomSpinner containerClassname="min-height" loaderClassname="mini" />
				</If>
				<If condition={!tripsLoading || wizardData.tabSelected !== 'freight'}>
					{/* eslint-disable-next-line no-nested-ternary */}
					<h6 className="from-price">
						{filteredFerryList.length > 0
							? wizardData.tabSelected === 'freight'
								? `${filteredFerryList.length} ${filteredFerryList.length > 1 ? 'trips' : 'trip'}`
								: 'From'
							: 'No trips'}
					</h6>
				</If>
				<If condition={wizardData.tabSelected !== 'freight'}>
					<h1 className="from-price">
						<If condition={filteredFerryList.length === 0}>-</If>
						<If condition={filteredFerryList.length > 0}>
							<If condition={pricesLoading || Object.keys(filteredPriceByFerryTrip).length === 0}>
								<CustomSpinner containerClassname="min-height" loaderClassname="mini" />
							</If>
							<If condition={!pricesLoading && Object.keys(filteredPriceByFerryTrip).length > 0}>
								{formatPriceDisplay(
									getMinimumFerryTicketPriceFromListOfFerryTrips(filteredPriceByFerryTrip),
									wizardData.wizardMode === wizardModeOptions.ALTERATION,
									false,
								)}
							</If>
						</If>
					</h1>
				</If>
			</div>
		);
		const tabComponent = (
			<div className="trip-card-column-container">
				<If condition={ferryTrips !== null}>
					<FilteredTripCards
						list={filteredFerryList}
						wizardData={wizardData}
						departure={departure}
						saveChanges={setWizardData}
						priceByFerryTrip={priceByFerryTrip}
						classPricesByFerryTrip={classPricesByFerryTrip}
						loading={pricesLoading}
						refreshTrips={refreshTrips}
					/>
				</If>
				<If condition={ferryTrips === null}>
					<LottieSpinner />
				</If>
			</div>
		);
		/*
			A day in the past, for a booker who is not staff.

			This used to read `currentDay < new Date() && currentDay.getDate() < new Date().getDate()`.
			`getDate()` is the day of the MONTH, so the second clause compared 30 against 2 whenever
			the rail straddled a month end — and every date in the previous month came back
			selectable while the ones in this month were correctly barred. On 2 October that put
			`30 WED` on offer and refused `1 THU`, two cells apart.

			Compared whole days, so a sailing later today is still bookable: `isBefore` against the
			start of today is false for every hour of it.
		*/
		// Whether the day is behind us, and separately whether THIS reader may open it. Staff may;
		// customers may not. Both are shown that it is past — only one is stopped.
		const isPast = moment(currentDay).isBefore(moment().startOf('day'), 'day');
		const disabled = isStaff ? false : isPast;

		listOfTabs.push({
			component: tabComponent,
			name: tabName,
			key: currentDay.toString(),
			disabled,
		});

		/*
			The cheapest this party can travel on this day, in any class on any of its sailings.
			A sailing with no price yet is absent from the map and dropped; a zero is a real fare —
			a complimentary or exempt customer travels free — and reads `From $0.00`, as its card does.

			On an edit the question changes with it: the booking is paid for, so what a day is worth
			comparing on is the smallest difference it would make to the bill — which the server has
			already priced per sailing, against this booking, for the cards below. A zero is kept
			there, because "this day costs you nothing extra" is the best answer the rail can give.
		*/
		const dayChanges = filteredFerryList
			.map(trip => {
				const sailingChange = priceByFerryTrip[trip.id];
				if (sailingChange === undefined || sailingChange === null) {
					return null;
				}
				// The server's figure is the sailing at the party's current fare mix. The cells below
				// also offer moving everyone into one fare type (FareTripCard's changeFor), so the
				// cheapest of those is weighed too — otherwise a day with a cheaper fare type reads as
				// `$0.00` above a `-$60.00` cell.
				const classPrices = classPricesByFerryTrip?.[trip.id] ?? [];
				const currentTotal = splitPartyTotal(classPrices, passengerFaresByLeg(wizardData, legType));
				const cheapest = cheapestClassTotal(classPrices, passengerCounts);
				return currentTotal !== null && cheapest !== null
					? Math.min(sailingChange, sailingChange + cheapest - currentTotal)
					: sailingChange;
			})
			.filter((price): price is number => price !== undefined && price !== null);
		// A sailing on a vessel with no fare types has no class prices to take the cheapest of, so it is
		// priced as its plain card is — otherwise a day whose only sailing is one of those read
		// `No trips` above a sailing that could be booked.
		const dayFares = filteredFerryList
			.map(trip => {
				const classPrices = classPricesByFerryTrip?.[trip.id] ?? [];
				return classPrices.length > 0
					? cheapestClassTotal(classPrices, passengerCounts)
					: (priceByFerryTrip[trip.id] ?? null);
			})
			.filter((price): price is number => price !== null && price !== undefined);

		const isChange = wizardData.wizardMode === wizardModeOptions.ALTERATION;
		// eslint-disable-next-line no-nested-ternary
		const dayPrice = isChange
			? dayChanges.length > 0
				? Math.min(...dayChanges)
				: null
			: dayFares.length > 0
				? Math.min(...dayFares)
				: null;

		days.push({
			date: currentDay,
			price: dayPrice,
			hasTrips: filteredFerryList.length > 0,
			disabled,
			isPast,
			isChange,
			tripCount: filteredFerryList.length,
			// Freight is quoted rather than listed, so its sailings carry no fare for the rail to
			// print — the day counts them instead, which is what the tab strip above it used to do.
			hidePrice: wizardData.tabSelected === 'freight',
		});
	}

	/**
	 * A vessel with priced classes shows one day at a time, under a rail of days and above a column
	 * per class.
	 *
	 * <p>The rail is not the tab strip. The tabs carried a cheapest-per-class figure for every day,
	 * which is the same set of numbers the columns below give for every sailing — the smaller set, in
	 * the worse place, a tab the width of a date. The rail carries one "from" per day, which is the
	 * question a day strip is actually asked: is another day cheaper. Comparing the classes is left
	 * to the columns, which have the room for it.</p>
	 *
	 * <p>Everything else keeps the tab strip: a vessel with no classes has one price per day anyway,
	 * which is exactly what a week of tabs is good at.</p>
	 */
	/*
		Everything the empty-day panel needs, worked out before the branch below rather than inside
		it — a hook cannot be called from inside an `if`, and the lookahead is a hook.
	*/
	const dayIndex = Math.min(Math.max(currentTab ?? 2, 0), Math.max(listOfTabs.length - 1, 0));

	/*
		The two directions, kept apart.

		They used to be one answer: the first day ahead with sailings, falling back to the nearest
		one behind. The fallback is worth offering — a day already on screen that still sails is a
		single tap — but it was being announced as "the next sailing", which on a Friday looking
		back at Wednesday is not true and reads as the screen being confused. They are now two
		facts, each said in its own words, and either may be absent.

		`disabled` carries "already gone" for a booker, so an earlier day that survives it is one
		that has not departed and can still be booked.
	*/
	const laterIndex = days.findIndex((day, index) => index > dayIndex && day.hasTrips && !day.disabled);
	const earlierIndex =
		[...days]
			.map((day, index) => ({ day, index }))
			.reverse()
			.find(x => x.index < dayIndex && x.day.hasTrips && !x.day.disabled)?.index ?? -1;

	const laterDay = laterIndex >= 0 ? { day: days[laterIndex], index: laterIndex } : null;
	const earlierDay = earlierIndex >= 0 ? { day: days[earlierIndex], index: earlierIndex } : null;

	const lookahead = useNextSailingQuery({
		fromLocationId: wizardData.fromLocationId,
		toLocationId: wizardData.toLocationId,
		// From the end of the rail, not from the day being looked at: everything in between is
		// already on screen and has already been read.
		afterDate: days[days.length - 1]?.date ?? (departureReturnDate as Date),
		isDeparture: departure,
		vehiclesOnly: wizardData.tabSelected === 'vehicle',
		// Only when the rail has nothing AHEAD to offer. A day behind the booker is not an answer to
		// "when does this route next run", so finding one does not stop the question being asked.
		enabled: !!days[dayIndex] && !days[dayIndex].hasTrips && !laterDay,
	});

	if (!USE_LEGACY_DAY_TABS) {
		/**
		 * Move the rail onto a date it is not currently showing.
		 *
		 * <p>Both dates are set, because the rail is built from the leg's own browsing date and the
		 * selection date is what the sailings below are filtered by — moving one without the other
		 * lands the booker on a day the rail is not drawn around.</p>
		 */
		const goToDate = (date: Date) => {
			const newData = new BookingWizardData({ ...wizardData });
			if (departure) {
				newData.startDate = date;
				newData.ticketSelectionStartDate = date;
				// As the rail's own arrows do: a departure moved past the return takes the return with it.
				unselectInvalidDateTicket(newData);
			} else {
				newData.endDate = date;
				newData.ticketSelectionEndDate = date;
			}
			setWizardData(newData);
			// The rail redraws around the new date, so the day in the middle of it is the one asked
			// for — which is where the tabs count from.
			setCurrentTab?.(2);
		};

		// Moving to a day also moves the wizard's own selection date, which is what the sailings
		// below are filtered by — the same thing the tabs' onTabClicked does.
		const selectDay = (index: number) => {
			setCurrentTab?.(index);
			const newData = new BookingWizardData({ ...wizardData });
			if (departure) {
				newData.ticketSelectionStartDate = moment(listOfTabs[index].key).toDate();
				keepReturnDayAfterDeparture(newData);
			} else {
				newData.ticketSelectionEndDate = moment(listOfTabs[index].key).toDate();
			}
			setWizardData(newData);
		};

		/*
			The day is showing nothing — but that is two different states.

			`hasTrips` is false while the window's sailings are still on their way as well as when
			the route genuinely does not run, and the panel below treats it as a verdict: it names
			the date, says where the closest sailing is, and offers buttons to it. Printed over a
			request that has not come back yet, that is simply wrong — the booker is told there are
			no sailings on a day that may be full of them.
		*/
		const dayShowsNothing = !!days[dayIndex] && !days[dayIndex].hasTrips;

		return (
			/* `--fares` is a historical name: it drops the container's own card chrome, which the
			   table below brings its own version of. Every booking type uses it now. */
			<div className="trip-selection-container trip-selection-container--fares">
				{/*
					Named, not counted. "Your travellers are in different fare types" on its own
					leaves the booker to open the seating step to find out which — and the answer is
					four short lines. It sits outside the table's frame because it is about the
					booking, not about the days or the sailings inside it.
				*/}
				{mixedParty && mixedParty.length > 0 && (
					<div className="fare-mix">
						{/*
							A letter in a disc, the way the seat plan's "A" badge is drawn, rather than
							an outline icon. At the size this sits it has to read as a mark on the
							panel and not as a control the booker could press.
						*/}
						<span className="fare-mix__mark" aria-hidden="true">
							i
						</span>
						<div className="fare-mix__body">
							<span className="fare-mix__title">Your travellers are in different fare types</span>
							{/*
								Who is in each fare type, one row per fare type rather than one per
								traveller — a party of four in two fare types is two facts.

								Counted by passenger type at every width. The row used to carry a
								`4 ×` for the phone and the travellers' names for the desktop, which
								was two ways of saying the same thing and neither of them safe on a
								large booking: the names grew with the party until they were most of
								the panel.
							*/}
							<ul className="fare-mix__list">
								{mixedParty.map((group, index) => (
									<li key={group.passengerClassId ?? 'none'} className="fare-mix__row">
										{/*
											The fare type's own colour, as a dot rather than a tinted
											chip. It is the same mark the columns, the seat plan and
											the seating step put beside that fare type, so a booker
											reading this panel already knows which column it means —
											and four filled chips in four class colours is a paint
											chart where a sentence should be.
										*/}
										<span
											className="fare-mix__dot"
											style={{ background: classColour(index, group.colour) }}
										/>
										<span className="fare-mix__who">{group.summary}</span>
										<span className="fare-mix__fare">{group.name}</span>
									</li>
								))}
							</ul>
							<span className="fare-mix__note">
								<FormattedMessage
									id="fare-mix__keep-or-pick__label"
									defaultMessage="Keep the mix, or pick one fare type to move everyone to."
								/>
								{/*
									Where they would go to do it. Left off the phone, where the panel
									sits directly above a column of full-width rows and the sentence
									is one more thing between the booker and them.
								*/}
								<span className="fare-mix__aside">
									{' '}
									<FormattedMessage
										id="fare-mix__change-at-seating__label"
										defaultMessage="Individual fare types can still be changed at the seating step."
									/>
								</span>
							</span>
						</div>
					</div>
				)}

				{/*
					One frame around the days and the sailings, because they are one decision. The
					rail is the table's first row, not a control sitting above it.
				*/}
				<div className="fare-table">
					<FareDayRail
						days={days}
						selectedIndex={dayIndex}
						onSelect={selectDay}
						onStep={changeDateByWeek}
						backDisabled={isStaff ? false : checkLeftDate(departureReturnDate as Date, 1)}
					/>

					{/*
						A day the route does not run is a dead end, and the rail alone leaves the booker
						to work out which of the other four to try. Naming the next day that sails —
						and its price — makes it one tap.
					*/}
					{dayShowsNothing && tripsLoading && (
						<div className="fare-empty">
							<span className="fare-empty__title">
								<FormattedMessage
									id="trip-option__searching-trips__label"
									defaultMessage="Searching for trips…"
								/>
							</span>
						</div>
					)}
					{dayShowsNothing && !tripsLoading && (
						<div className="fare-empty">
							<span className="fare-empty__title">
								<FormattedMessage
									id="trip-option__no-sailings-on__label"
									defaultMessage="No sailings on {date}"
									values={{ date: moment(days[dayIndex].date).format('ddd D MMM') }}
								/>
							</span>
							{/*
								The heading above has already said there is nothing on this date, so
								this line is spent on the only thing the booker still needs: when the
								next sailing is.

								Three answers, in the order they cost. A day inside the rail is free
								— it is on screen. Beyond it the route is asked directly, which takes
								a moment and is worth saying so rather than leaving the panel looking
								finished. And a route with nothing for the next year is told
								plainly, because "try another week" would be eight wrong guesses.
							*/}
							<span className="fare-empty__text">
								{(() => {
									/*
										The line names no dates. The buttons under it already do —
										with the price and the direction — so a sentence naming them
										again said each date twice, in two formats, in a panel whose
										heading has just named a third. What is left for prose is
										the one thing a button cannot be: an answer when there is
										nothing to offer.
									*/
									if (lookahead.isLoading) {
										return intl.formatMessage({
											id: 'trip-option__lookahead-searching__label',
											defaultMessage: 'Looking for the next sailing on this route…',
										});
									}

									const onOffer = [laterDay, lookahead.date, earlierDay].filter(x => !!x).length;
									if (onOffer === 0) {
										return intl.formatMessage({
											id: 'trip-option__no-sailings-next-year__label',
											defaultMessage: 'This route has no more sailings in the next year.',
										});
									}

									// Not "this route doesn't sail every day": the day may be empty
									// because the route does not run, because its sailings have gone,
									// or because they are full — and this line cannot tell which.
									return intl.formatMessage(
										{
											id: 'trip-option__closest-days__label',
											defaultMessage:
												'{count, plural, one {The closest day with sailings is below.} other {The closest days with sailings are below.}}',
										},
										{ count: onOffer },
									);
								})()}
							</span>
							{/*
								The way to whatever the line above just named. A day inside the rail
								is one tap; a date beyond it moves the rail onto that date rather
								than stepping a week at a time towards it, which on a route that
								sails monthly is five taps of nothing.
							*/}
							{/*
								One control per day that is actually on offer, the one ahead first.
								A day inside the rail is a tap; a date beyond it moves the rail onto
								that date rather than stepping a week at a time towards it, which on
								a route that sails monthly is four taps of nothing.
							*/}
							<div className="fare-empty__actions">
								{(laterDay || (!lookahead.isLoading && lookahead.date)) && (
									<Button
										className="fare-empty__action"
										colors={Colors.Primary}
										display={Display.Solid}
										newStyles
										onClick={() =>
											laterDay
												? selectDay(laterDay.index)
												: goToDate(lookahead.date!)
										}
									>
										{laterDay
											? showDayLabel(laterDay.day.date, laterDay.day.price)
											: showDayLabel(lookahead.date!, null)}
										{/*
											Which way the rail will move. Two buttons naming two
											dates leave the reader to work out which is forwards
											from the dates themselves, and on a booking that crosses
											a month that is arithmetic rather than reading.
										*/}
										<span className="fare-empty__way" aria-hidden="true">
											›
										</span>
									</Button>
								)}

								{earlierDay && (
									<Button
										className="fare-empty__action fare-empty__action--earlier"
										colors={Colors.Primary}
										display={Display.Outline}
										newStyles
										onClick={() => selectDay(earlierDay.index)}
									>
										<span className="fare-empty__way" aria-hidden="true">
											‹
										</span>
										{showDayLabel(earlierDay.day.date, earlierDay.day.price)}
									</Button>
								)}
							</div>
						</div>
					)}
					{!dayShowsNothing && listOfTabs[dayIndex]?.component}
				</div>
			</div>
		);
	}

	return (
		<div className="trip-selection-container">
			<Tabs
				tabs={listOfTabs}
				leftTab={leftTab}
				rightTab={rightTab}
				defaultTab={2}
				currentTab={currentTab}
				scrollSelectedIntoView
				showScrollArrows
				onTabClicked={action((tabIndex, event) => {
					setCurrentTab?.(tabIndex);
					const newData = new BookingWizardData({ ...wizardData });
					if (departure) {
						newData.ticketSelectionStartDate = moment(listOfTabs[tabIndex].key).toDate();
						keepReturnDayAfterDeparture(newData);
					} else {
						newData.ticketSelectionEndDate = moment(listOfTabs[tabIndex].key).toDate();
					}
					setWizardData(newData);
				})}
			/>
		</div>
	);
}
